/**
 * defer-mount.js
 * ---------------------------------------------------------------------------
 * Zero-CLS progressive mounting for below-the-fold sections.
 *
 * Two independent mechanisms, both driven by one IntersectionObserver:
 *
 *   1. HYDRATE  (data-defer="hydrate")
 *      Markup is already in the page inside the wrapper. The wrapper is
 *      height-reserved and `content-visibility: auto`, so the browser skips
 *      layout + paint for it until it is near the viewport. On intersect we
 *      release it and fade in. No network request, no SEO risk, no reflow.
 *      This is the default and the safe choice for anything with links or
 *      text that matters (footer, nav-bearing sections).
 *
 *   2. FETCH    (data-defer="fetch")
 *      Wrapper ships empty at a reserved height. On intersect we pull the
 *      real HTML from Shopify's Section Rendering API and mount it. Use only
 *      for heavy, purely-decorative blocks (reviews, social feeds, marketing
 *      banners) where the extra round trip is worth the saved bytes.
 *
 * Both paths guarantee:
 *   - reserved height before mount  -> no scrollbar jump, no page snap
 *   - rootMargin 300px 0px          -> content is ready before it is seen
 *   - opacity 0 -> 1 over 200ms     -> no pop-in flicker
 *   - height released only AFTER content is in the DOM and measured
 *
 * Degrades to "render everything immediately" when JS or IO is unavailable.
 */
(function () {
  'use strict';

  // Tells the inline watchdog in theme.liquid that this file arrived and
  // parsed. If this line never runs, the watchdog reveals every deferred
  // section 2s after load rather than leaving them invisible.
  window.__deferMountReady = true;

  var SELECTOR = '[data-defer]';
  var ROOT_MARGIN = '300px 0px';

  // No IntersectionObserver (or JS disabled -> this file never runs anyway):
  // reveal everything synchronously so nothing is ever lost.
  if (!('IntersectionObserver' in window)) {
    document.addEventListener('DOMContentLoaded', function () {
      var all = document.querySelectorAll(SELECTOR);
      for (var i = 0; i < all.length; i++) revealNow(all[i]);
    });
    return;
  }

  /* ----------------------------------------------------------------------- */

  function revealNow(wrapper) {
    wrapper.removeAttribute('data-defer-pending');
    wrapper.setAttribute('data-defer-state', 'mounted');
    releaseHeight(wrapper);
  }

  /**
   * Drop the reserved height once the real content is in place.
   *
   * Ordering matters. We release on the next animation frame so the browser
   * has already laid the new content out; swapping min-height in the same
   * task as the DOM insertion is what produces the visible "snap".
   */
  function releaseHeight(wrapper) {
    requestAnimationFrame(function () {
      wrapper.style.containIntrinsicSize = '';
      wrapper.style.contentVisibility = 'visible';
      wrapper.style.minHeight = '0px';
    });
  }

  function fadeIn(wrapper) {
    // Force a style flush so the transition actually runs from 0.
    // eslint-disable-next-line no-unused-expressions
    wrapper.offsetHeight;
    wrapper.setAttribute('data-defer-state', 'mounted');
  }

  /* --- mechanism 1: hydrate in place ------------------------------------- */

  function hydrate(wrapper) {
    wrapper.removeAttribute('data-defer-pending');
    fadeIn(wrapper);
    releaseHeight(wrapper);
    announce(wrapper);
  }

  /* --- mechanism 2: fetch via Section Rendering API ----------------------- */

  function fetchSection(wrapper) {
    var url = wrapper.getAttribute('data-defer-url');
    if (!url) {
      // Misconfigured: fail open rather than leaving a hole in the page.
      hydrate(wrapper);
      return;
    }

    wrapper.setAttribute('data-defer-state', 'loading');

    fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        if (!res.ok) throw new Error('defer-mount: HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        var target = wrapper.querySelector('[data-defer-slot]') || wrapper;

        // The Section Rendering API returns the section wrapper too. Unwrap it
        // so we don't end up with a duplicate #shopify-section-* id in the DOM.
        var parsed = document.createElement('div');
        parsed.innerHTML = html;
        var inner = parsed.querySelector('.shopify-section') || parsed;

        target.innerHTML = inner.innerHTML;

        // <script> injected via innerHTML is inert. Re-create so it executes.
        var scripts = target.querySelectorAll('script');
        for (var i = 0; i < scripts.length; i++) {
          var old = scripts[i];
          var s = document.createElement('script');
          for (var a = 0; a < old.attributes.length; a++) {
            s.setAttribute(old.attributes[a].name, old.attributes[a].value);
          }
          s.textContent = old.textContent;
          old.parentNode.replaceChild(s, old);
        }

        wrapper.removeAttribute('data-defer-pending');
        fadeIn(wrapper);
        releaseHeight(wrapper);
        announce(wrapper);
      })
      .catch(function (err) {
        if (window.console && console.warn) console.warn(err);
        // Never leave a blank reserved box on screen.
        wrapper.setAttribute('data-defer-state', 'error');
        releaseHeight(wrapper);
      });
  }

  /* ----------------------------------------------------------------------- */

  /**
   * Let other scripts know new DOM exists. Slick carousels, review widgets and
   * anything bound at DOMContentLoaded need a hook to initialise late content.
   */
  function announce(wrapper) {
    wrapper.dispatchEvent(
      new CustomEvent('defer-mount:mounted', {
        bubbles: true,
        detail: { id: wrapper.getAttribute('data-defer-id') || null }
      })
    );
  }

  var observer = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!entry.isIntersecting) continue;

        var wrapper = entry.target;
        observer.unobserve(wrapper);

        if (wrapper.getAttribute('data-defer') === 'fetch') {
          fetchSection(wrapper);
        } else {
          hydrate(wrapper);
        }
      }
    },
    { rootMargin: ROOT_MARGIN, threshold: 0 }
  );

  function observeAll(root) {
    var nodes = (root || document).querySelectorAll(SELECTOR + '[data-defer-pending]');
    for (var i = 0; i < nodes.length; i++) observer.observe(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observeAll(); });
  } else {
    observeAll();
  }

  // Theme editor: sections are re-rendered live, so re-arm the observer.
  document.addEventListener('shopify:section:load', function (e) {
    observeAll(e.target);
  });

  // In the customiser, never hide anything - merchants must see their edits.
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('DOMContentLoaded', function () {
      var all = document.querySelectorAll(SELECTOR);
      for (var i = 0; i < all.length; i++) {
        if (all[i].getAttribute('data-defer') === 'fetch') {
          fetchSection(all[i]);
        } else {
          hydrate(all[i]);
        }
      }
    });
  }
})();
